import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import RefreshIcon from "../../../../components/icons/RefreshIcon";
import Loader from "../../../../components/loader/Loader";
import PrimeBadge from "../../../../components/primeBadge/PrimeBadge";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import Button from "../../../../components/button/Button";
import {
  fetchReportedComments,
  fetchCommentReports,
  hideComment,
  setSelectedComment,
  setSelectedTournament,
} from "../../../../features/tournamentCommentReports/tournamentCommentReportsSlice";
import { showConfirmationModal } from "../../../../features/confirmationModal/ConfirmationModalSlice";
import styles from "./tournamentsCommentsReports.module.css";
import TrophyIcon from "../../../../components/icons/TrophyIcon";

const REASON_LABELS = {
  spam: "إعلان مزعج",
  harassment: "مضايقة",
  hate: "كراهية",
  nudity: "محتوى غير لائق",
  violence: "عنف",
  misinformation: "معلومات خاطئة",
  selfHarm: "إيذاء النفس أو أفكار انتحارية",
  other: "أخرى",
};

const TournamentsCommentsReports = () => {
  const dispatch = useDispatch();
  const {
    reportedComments,
    selectedComment,
    commentReports,
    tournaments,
    currentPage,
    hasMore,
    fetchReportsStatus,
    actionStatus,
    selectedTournamentId,
  } = useSelector((state) => state.tournamentCommentReports);
  const { rolePermissions } = useSelector((state) => state.user);
  const can = (permission) => rolePermissions.includes(permission);

  const commentsListRef = useRef();
  const [showReports, setShowReports] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const prevActionStatus = useRef(actionStatus);

  // Fetch initial data
  useEffect(() => {
    if (fetchReportsStatus === "idle") {
      dispatch(fetchReportedComments({ status: "open", page: 1 }));
    }
  }, [dispatch, fetchReportsStatus]);

  // Refresh list after successful actions
  useEffect(() => {
    if (
      prevActionStatus.current === "loading" &&
      actionStatus === "succeeded"
    ) {
      // Refetch the current view
      dispatch(
        fetchReportedComments({
          status: "open",
          tournamentId: selectedTournamentId,
          page: 1,
        }),
      );
    }
    prevActionStatus.current = actionStatus;
  }, [dispatch, actionStatus, selectedTournamentId]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = commentsListRef.current;
    if (!container || fetchReportsStatus === "loading" || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      hasMore &&
      currentPage
    ) {
      dispatch(
        fetchReportedComments({
          status: "open",
          tournamentId: selectedTournamentId,
          page: currentPage + 1,
        }),
      );
    }
  }, [
    dispatch,
    fetchReportsStatus,
    hasMore,
    currentPage,
    selectedTournamentId,
  ]);

  useEffect(() => {
    const container = commentsListRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const handleRefreshReports = useCallback(async () => {
    setIsRefreshing(true);
    commentsListRef.current?.scrollTo?.({
      top: 0,
      behavior: "smooth",
    });

    try {
      const refreshRequests = [
        dispatch(
          fetchReportedComments({
            status: "open",
            tournamentId: selectedTournamentId,
            page: 1,
          }),
        ),
      ];

      if (selectedComment) {
        refreshRequests.push(
          dispatch(
            fetchCommentReports(
              selectedComment.commentId || selectedComment.comment?._id,
            ),
          ),
        );
      }

      await Promise.all(refreshRequests);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, selectedComment, selectedTournamentId]);

  // Handle tournament selection
  const handleTournamentSelect = (tournamentId) => {
    dispatch(setSelectedTournament(tournamentId));
    dispatch(fetchReportedComments({ status: "open", tournamentId, page: 1 }));
  };

  // Handle comment selection
  const handleCommentSelect = async (comment) => {
    dispatch(setSelectedComment(comment));
    setShowReports(false);
    // Fetch detailed reports for this comment
    await dispatch(
      fetchCommentReports(comment.commentId || comment.comment?._id),
    );
    setShowReports(true);
  };

  // Handle hide comment
  const handleHideComment = () => {
    if (!selectedComment) return;
    const commentId = selectedComment.commentId || selectedComment.comment?._id;
    dispatch(
      showConfirmationModal({
        message: "هل أنت متأكد من إخفاء هذا التعليق؟",
        actionType: "hideComment",
        data: { commentId, isHidden: true },
      }),
    );
  };

  // Handle delete comment
  const handleDeleteComment = () => {
    if (!selectedComment) return;
    const commentId = selectedComment.commentId || selectedComment.comment?._id;
    dispatch(
      showConfirmationModal({
        message:
          "هل أنت متأكد من حذف هذا التعليق؟ سيتم حذف التعليق والردود عليه.",
        actionType: "deleteComment",
        data: { commentId },
      }),
    );
  };

  // Handle dismiss reports
  const handleDismissReports = () => {
    if (!selectedComment) return;
    const commentId = selectedComment.commentId || selectedComment.comment?._id;
    dispatch(
      showConfirmationModal({
        message: "هل أنت متأكد من رفض جميع التقارير لهذا التعليق؟",
        actionType: "dismissReports",
        data: { commentId },
      }),
    );
  };

  // The reported comment's tournament (the reports list carries its name).
  const tournamentNameOf = (item) => item?.tournament?.name || item?.comment?.tournamentName || "";

  // Get total reports count for "All Tournaments"
  const allTournamentsReportsCount = reportedComments.reduce(
    (sum, item) => sum + (item.reportsCount || 0),
    0,
  );

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.headerTitleGroup}>
          <TextWrapper textStyle="textTitle">تقارير تعليقات البطولات</TextWrapper>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => {
              Promise.resolve(handleRefreshReports()).catch(() => {});
            }}
            disabled={fetchReportsStatus === "loading" || isRefreshing}
            title="تحديث تقارير التعليقات"
            aria-label="تحديث تقارير التعليقات"
          >
            {isRefreshing ? (
              <Loader size={18} color="var(--color-accent-primary)" />
            ) : (
              <RefreshIcon className={styles.refreshIcon} />
            )}
          </button>
        </div>
      </div>
      <div className={styles.displayContainerWrapper}>
        <DisplayContainer>
          <div className={styles.reportsWrapper}>
            {/* Left Side - Tournaments and Comments List */}
            <div className={styles.leftSide}>
              {/* Tournaments List */}
              <div className={styles.tournamentsList}>
                <TextWrapper textStyle="textMid">البطولات</TextWrapper>
                <div
                  className={`${styles.tournamentItem} ${
                    selectedTournamentId === null ? styles.active : ""
                  }`}
                  onClick={() => handleTournamentSelect(null)}
                >
                  <TextWrapper textStyle="textSmall">جميع البطولات</TextWrapper>
                  <TextWrapper textStyle="textXSmall">
                    ({allTournamentsReportsCount} تقرير)
                  </TextWrapper>
                </div>
                {tournaments.map((tournament) => (
                  <div
                    key={tournament.tournamentId}
                    className={`${styles.tournamentItem} ${
                      selectedTournamentId === tournament.tournamentId
                        ? styles.active
                        : ""
                    }`}
                    onClick={() =>
                      handleTournamentSelect(tournament.tournamentId)
                    }
                  >
                    <TextWrapper textStyle="textSmall">
                      {tournament.name || `بطولة ${tournament.tournamentId.slice(0, 8)}...`}
                    </TextWrapper>
                    <TextWrapper textStyle="textXSmall">
                      ({tournament.reportsCount} تقرير)
                    </TextWrapper>
                  </div>
                ))}
              </div>

              {/* Comments List */}
              <div className={styles.commentsList} ref={commentsListRef}>
                <TextWrapper textStyle="textMid">
                  التعليقات المبلغ عنها
                </TextWrapper>
                {reportedComments.map((item) => {
                  const commentId = item.commentId || item.comment?._id;
                  const isSelected =
                    selectedComment?.commentId === commentId ||
                    selectedComment?.comment?._id === commentId;

                  return (
                    <div
                      key={commentId}
                      className={`${styles.commentItem} ${
                        isSelected ? styles.active : ""
                      }`}
                      onClick={() => handleCommentSelect(item)}
                    >
                      {tournamentNameOf(item) && (
                        <span className={styles.commentTournament}>
                          <TrophyIcon className={styles.commentTournamentIcon} />
                          <span className={styles.commentTournamentName} dir="auto">
                            {tournamentNameOf(item)}
                          </span>
                        </span>
                      )}
                      <TextWrapper textStyle="textSmall">
                        {item.comment?.text?.substring(0, 50) ||
                          "تعليق بدون نص"}
                        {item.comment?.text?.length > 50 ? "..." : ""}
                      </TextWrapper>
                      <TextWrapper textStyle="textXSmall">
                        {item.reportsCount} تقرير
                      </TextWrapper>
                      {item.comment?.isHidden && (
                        <div className={styles.hiddenStatus}>
                          <TextWrapper textStyle="textXSmall">
                            (مخفي)
                          </TextWrapper>
                        </div>
                      )}
                    </div>
                  );
                })}
                {fetchReportsStatus === "loading" && (
                  <div className={styles.loaderContainer}>
                    <Loader size={30} color="var(--color-text-primary)" />
                  </div>
                )}
                {reportedComments.length === 0 &&
                  fetchReportsStatus === "succeeded" && (
                    <TextWrapper textStyle="textMid">
                      لا توجد تعليقات مبلغ عنها
                    </TextWrapper>
                  )}
              </div>
            </div>

            {/* Right Side - Comment Details */}
            <div className={styles.rightSide}>
              {!selectedComment ? (
                <div className={styles.emptyState}>
                  <TextWrapper textStyle="textTitle">
                    اختر تعليقاً لعرض التفاصيل
                  </TextWrapper>
                </div>
              ) : (
                <div className={styles.commentDetails}>
                  {/* Comment Text */}
                  <div className={styles.commentTextSection}>
                    <TextWrapper textStyle="textMid">نص التعليق:</TextWrapper>
                    <div className={styles.commentText}>
                      <TextWrapper textStyle="textSmall">
                        {selectedComment.comment?.text || "لا يوجد نص"}
                      </TextWrapper>
                    </div>
                  </div>

                  {/* Tournament */}
                  <div className={styles.commentAuthorSection}>
                    <TextWrapper textStyle="textMid">البطولة:</TextWrapper>
                    <TextWrapper textStyle="textSmall">
                      {tournamentNameOf(selectedComment) || "بطولة غير معروفة"}
                    </TextWrapper>
                  </div>

                  {/* Comment Author */}
                  <div className={styles.commentAuthorSection}>
                    <TextWrapper textStyle="textMid">المستخدم:</TextWrapper>
                    <TextWrapper textStyle="textSmall">
                      {selectedComment.commentUser?.name ||
                        selectedComment.commentUser?.userName ||
                        "مستخدم غير معروف"}
                    </TextWrapper>
                    <PrimeBadge
                      isPrime={selectedComment.commentUser?.isPrime}
                      primeTier={selectedComment.commentUser?.primeTier}
                      size="small"
                    />
                  </div>

                  {/* Reports Summary */}
                  <div className={styles.reportsSummarySection}>
                    <TextWrapper textStyle="textMid">
                      عدد التقارير: {selectedComment.reportsCount || 0}
                    </TextWrapper>
                    {selectedComment.reasons &&
                      selectedComment.reasons.length > 0 && (
                        <div className={styles.reasonsList}>
                          <TextWrapper textStyle="textSmall">
                            أسباب الإبلاغ:
                          </TextWrapper>
                          {selectedComment.reasons.map((reason, index) => (
                            <TextWrapper key={index} textStyle="textXSmall">
                              - {REASON_LABELS[reason] || reason}
                            </TextWrapper>
                          ))}
                        </div>
                      )}
                  </div>

                  {/* Detailed Reports */}
                  {showReports && commentReports.length > 0 && (
                    <div className={styles.detailedReportsSection}>
                      <TextWrapper textStyle="textMid">
                        تفاصيل التقارير:
                      </TextWrapper>
                      <div className={styles.reportsList}>
                        {commentReports.map((report, index) => (
                          <div
                            key={report._id || index}
                            className={styles.reportItem}
                          >
                            <TextWrapper textStyle="textSmall">
                              <strong>المبلغ:</strong>{" "}
                              {report.reporterId?.name ||
                                report.reporterId?.userName ||
                                "مستخدم غير معروف"}
                              <PrimeBadge
                                isPrime={report.reporterId?.isPrime}
                                primeTier={report.reporterId?.primeTier}
                                size="small"
                              />
                            </TextWrapper>
                            <TextWrapper textStyle="textXSmall">
                              <strong>السبب:</strong>{" "}
                              {REASON_LABELS[report.reason] || report.reason}
                            </TextWrapper>
                            {report.note && (
                              <TextWrapper textStyle="textXSmall">
                                <strong>ملاحظة:</strong> {report.note}
                              </TextWrapper>
                            )}
                            <TextWrapper textStyle="textXSmall">
                              <strong>التاريخ:</strong>{" "}
                              {new Date(report.createdAt).toLocaleDateString(
                                "ar-SA",
                              )}
                            </TextWrapper>
                            <TextWrapper textStyle="textXSmall">
                              <strong>الحالة:</strong>{" "}
                              {report.status === "open"
                                ? "مفتوح"
                                : report.status === "resolved"
                                  ? "تم الحل"
                                  : "مرفوض"}
                            </TextWrapper>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className={styles.actionsSection}>
                    {can("hide-comments") && (selectedComment.comment?.isHidden ? (
                      <Button
                        variant="greenVariant"
                        size="midSize"
                        functionallity={() => {
                          const commentId =
                            selectedComment.commentId ||
                            selectedComment.comment?._id;
                          dispatch(
                            hideComment({
                              commentId,
                              isHidden: false,
                              hideReason: null,
                            }),
                          );
                        }}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          إظهار التعليق
                        </TextWrapper>
                      </Button>
                    ) : (
                      <Button
                        variant="blackVariant"
                        size="midSize"
                        functionallity={handleHideComment}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          إخفاء التعليق
                        </TextWrapper>
                      </Button>
                    ))}
                    {can("delete-comments") && (
                      <Button
                        variant="redVariant"
                        size="midSize"
                        functionallity={handleDeleteComment}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          حذف التعليق
                        </TextWrapper>
                      </Button>
                    )}
                    {can("dismiss-comment-reports") && (
                      <Button
                        variant="blackVariant"
                        size="midSize"
                        functionallity={handleDismissReports}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          رفض التقارير
                        </TextWrapper>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DisplayContainer>
      </div>
    </div>
  );
};

export default TournamentsCommentsReports;
